import { config } from "@config/config";
import { datadogRum } from "@datadog/browser-rum";
import { formatDateTime, unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import PizZip from "pizzip";
import { useState } from "react";

import {
  ApiReportReportStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportSectionFieldsFieldTypeChoices,
  ApiReportTemplateFileUploadConfigChoices,
  ExportTypeEnum,
  ReportExportDataType,
  useGenerateReportPdfFromDocxMutation,
  useReportExportDataQuery,
  useReportStatusQuery,
} from "@api/graphql/types-and-hooks";
import {
  DsmSpecifierRow,
  TransformedData,
  dsmSeverityLevels,
  getFieldContentByFieldIdentifier,
  getSectionObjectBySectionIdentifier,
  organizeCatalightTreatmentPlanGoals,
  parseDsmSpecifiers,
  transformVinelandFormData,
  transformVinelandTableData,
} from "@components/PDFGenerator/Catalight/catalightUtils";
import {
  FieldMap,
  ImageDimensions,
  TemplateDataValue,
  base64StringToArrayBuffer,
  fetchImageAsBase64,
  markdownToDocxtemplater,
  processExportData,
  resolveImageDimensions,
  safeExpressionParser,
  selectHeaderLogoUrl,
  verifyUniqueFieldMapKeys,
} from "@components/PDFGenerator/docxTemplateUtils";
import {
  isWetSignatureContentEmpty,
  parseWetSignatureContent,
} from "@components/ReportField/inputs/wetSignatureContent";
import { convertReadableString } from "@components/forms/utils";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { ReportNode } from "@providers/ReportProvider";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

type SignatureCandidateField =
  | { fieldType?: ApiReportSectionFieldsFieldTypeChoices | null; content?: string | null }
  | null
  | undefined;

const isSignatureField = (field: SignatureCandidateField) =>
  field?.fieldType === ApiReportSectionFieldsFieldTypeChoices.TypedSignature ||
  field?.fieldType === ApiReportSectionFieldsFieldTypeChoices.WetSignature;

// The typed name a field holds, or "" when it holds none. A TYPED_SIGNATURE counts in any
// section; a WET_SIGNATURE only when it is in wetScope.
const getTypedSignatureName = (
  field: SignatureCandidateField,
  wetScope: ReadonlySet<SignatureCandidateField>,
): string => {
  if (field?.fieldType === ApiReportSectionFieldsFieldTypeChoices.TypedSignature) {
    const content = field.content ?? "";
    return content.trim() ? content : "";
  }
  if (field?.fieldType !== ApiReportSectionFieldsFieldTypeChoices.WetSignature || !wetScope.has(field)) return "";
  // content is the raw JSON envelope; drawn/uploaded values are image keys, not names.
  const { method, value } = parseWetSignatureContent(field.content);
  const typedValue = method === "typed" ? value ?? "" : "";
  return typedValue.trim() ? typedValue : "";
};

export const buildImageModuleOptions = (imageDimensions: Record<string, ImageDimensions>) => ({
  centered: true,
  //@ts-ignore: Ignoring the compiler and risking bugs because: we need to use the image module
  getImage: (tagValue: string) => base64StringToArrayBuffer(tagValue),
  //@ts-ignore: Ignoring the compiler and risking bugs because: we need to use the image module
  getSize: (_img: ArrayBuffer, _tagValue: string, tagName: string): [number, number] => {
    const dimensions = imageDimensions[tagName];
    if (!dimensions) return [350, 250];
    const { width, height } = dimensions;
    if (tagName !== "headerLogo") return [width, height];
    const maxWidth = 250;
    return width > maxWidth ? [maxWidth, Math.round((height * maxWidth) / width)] : [width, height];
  },
});

// When the image module can't be loaded, base64 image values would otherwise render as raw
// data URIs in the document — swap them for a readable placeholder instead.
export const replaceImageValuesWithPlaceholders = (
  templateData: Record<string, TemplateDataValue>,
  imageDimensions: Record<string, ImageDimensions>,
) => {
  for (const [key, value] of Object.entries(templateData)) {
    if (typeof value !== "string" || !value.startsWith("data:image/")) continue;
    const dimensions = imageDimensions[key];
    templateData[key] = dimensions ? `[${key} Image - ${dimensions.width}x${dimensions.height}]` : `[${key} Image]`;
  }
};

interface UseTemplateExportProps {
  reportData: ReportNode;
  exportType?: ExportTypeEnum;
  refetchReport?: () => Promise<ReportNode | undefined>;
  onExportComplete?: () => void;
}

interface UseTemplateExportReturn {
  /** Returns `true` on success, `false` on failure. */
  handleExport: () => Promise<boolean>;
  /** Generates the DOCX and uploads it to trigger PDF conversion, without polling or downloading. */
  generateAndUploadPdf: () => Promise<void>;
  isExporting: boolean;
  isLoading: boolean;
  isExportDataReady: boolean;
  exportError: string | null;
  clearExportError: () => void;
}

export const useTemplateExport = ({
  reportData,
  exportType = ExportTypeEnum.Docx,
  refetchReport,
  onExportComplete,
}: UseTemplateExportProps): UseTemplateExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const generateReportPdfFromDocxMutation = useGenerateReportPdfFromDocxMutation({});
  const { refetch: refetchReportStatus } = useReportStatusQuery({ reportId: reportData?.id ?? "" }, { enabled: false });

  const {
    data: reportExportData,
    isLoading,
    refetch: refetchReportExportData,
  } = useReportExportDataQuery(
    {
      reportId: reportData?.id ?? "",
    },
    { enabled: !!reportData && reportData.reportStatus === ApiReportReportStatusChoices.Completed },
  );
  const isExportDataReady = !!reportExportData?.reportExportData;
  const isCatalightTemplate =
    reportData.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();

  const font = convertReadableString(reportData?.reportTemplate?.exportTemplateFont) || "Public Sans";

  // Same rules as the worker export (PROA-2919): the first TYPED_SIGNATURE or typed
  // WET_SIGNATURE value wins. The reportWriter fallback is for legacy TYPED_SIGNATURE
  // only; over an unsaved WET_SIGNATURE it would make the report look signed.
  const getTypedSignatureValue = (): string => {
    const sections = unwrapConnection(reportData?.reportSections) || [];
    const allFields = sections.flatMap((section) => unwrapConnection(section?.sectionField) || []);
    // Wet signatures are read from the section keyed "signature" when it has a signature
    // field, else every section, since some templates key their signature section
    // differently. A typed wet value elsewhere (e.g. a guardian's) is not the signer's.
    const signatureSection = sections.find((section) => section?.sectionIdentifier === "signature");
    const signatureSectionFields = unwrapConnection(signatureSection?.sectionField) || [];
    const wetScope = new Set<SignatureCandidateField>(
      signatureSectionFields.some(isSignatureField) ? signatureSectionFields : allFields,
    );
    for (const field of allFields) {
      const name = getTypedSignatureName(field, wetScope);
      if (name) return name;
    }
    const wetFields = [...wetScope].filter(
      (field) => field?.fieldType === ApiReportSectionFieldsFieldTypeChoices.WetSignature,
    );
    if (wetFields.length > 0 && wetFields.every((field) => isWetSignatureContentEmpty(field?.content))) {
      return "";
    }
    return reportExportData?.reportExportData?.reportWriter ?? "";
  };

  const typedSignature = getTypedSignatureValue();
  const signatureName = (
    typedSignature ||
    reportExportData?.reportExportData?.signatureSection?.signatureName ||
    ""
  ).trim();
  // An empty bold span ("****") renders as literal asterisks, so a blank name gets a blank paragraph.
  const signatureValue = markdownToDocxtemplater(signatureName ? `**${signatureName}**` : "", "Monotype Corsiva");

  const reportType = reportData?.reportType;

  const getAssessmentWriteupsFieldMap = (): FieldMap => {
    const catalightAssessmentWriteupsEdges = getSectionObjectBySectionIdentifier(
      reportData?.reportSections?.edges,
      "Assessment_Writeups",
    )?.node?.sectionField.edges;

    return {
      ...transformVinelandTableData(
        getFieldContentByFieldIdentifier(catalightAssessmentWriteupsEdges, "catalight_vineland_scores_table"),
      ),
      ...transformVinelandFormData(
        getFieldContentByFieldIdentifier(catalightAssessmentWriteupsEdges, "catalight_vineland_metadata"),
      ),
    };
  };

  const getCatalightFieldMap = (
    resolvedExportData: ReportExportDataType,
  ): FieldMap | Record<string, TemplateDataValue> => {
    const catalightAdaptiveAssessmentWriteupsEdges = getSectionObjectBySectionIdentifier(
      reportData?.reportSections?.edges,
      "Adaptive_Assessments",
    )?.node?.sectionField.edges;
    const catalightClientDetailsEdges = getSectionObjectBySectionIdentifier(
      reportData?.reportSections?.edges,
      "Catalight_Client_Details",
    )?.node?.sectionField.edges;

    const exportData = processExportData(reportData, resolvedExportData, font) || {};

    if (reportType === ApiReportReportTypeChoices.DiagnosisEvaluation) {
      const dsmDomainAKey = Object.keys(exportData).find((key) => key.includes("Domain_A"));
      const dsmDomainBKey = Object.keys(exportData).find((key) => key.includes("Domain_B"));
      const dsmSpecifiersKey = Object.keys(exportData).find((key) => key.includes("Specifiers"));

      return {
        ...exportData,
        signatureDate: exportData.signatureDate ? formatDateTime(new Date(String(exportData.signatureDate))) : "",
        ...transformVinelandTableData(
          getFieldContentByFieldIdentifier(catalightAdaptiveAssessmentWriteupsEdges, "catalight_vineland_scores_table"),
        ),
        ...(dsmDomainAKey &&
          dsmDomainBKey &&
          dsmSeverityLevels((exportData[dsmDomainAKey] as string) || "", (exportData[dsmDomainBKey] as string) || "")),
        ...(dsmSpecifiersKey &&
          ({
            [dsmSpecifiersKey]: parseDsmSpecifiers((exportData[dsmSpecifiersKey] as DsmSpecifierRow[]) || []),
          } as unknown as TransformedData)),
      };
    }

    let signatureDate = "";
    if (exportData.signatureDate) {
      try {
        signatureDate = format(new Date(String(exportData.signatureDate)), "MM-dd-yyyy");
      } catch {
        signatureDate = String(exportData.signatureDate);
      }
    }

    return {
      ...exportData,
      mrn: getFieldContentByFieldIdentifier(catalightClientDetailsEdges, "MRN"),
      signatureName: signatureName,
      formattedSignatureName: signatureValue,
      signatureDate,
      ...getAssessmentWriteupsFieldMap(),
    };
  };

  /**
   * Fetches the appropriate header logo as a base64 string.
   * Uses organization logo if allowed and available, otherwise falls back to template logo.
   */
  const getHeaderLogoBase64 = async (resolvedExportData?: ReportExportDataType | null): Promise<string | null> => {
    const exportData = resolvedExportData ?? reportExportData?.reportExportData;
    const logoUrl = selectHeaderLogoUrl(
      exportData?.organizationLogoAllowed,
      exportData?.organizationLogoSignedUrl,
      exportData?.templateLogoSignedUrl,
    );

    if (!logoUrl) {
      return null;
    }

    const base64 = await fetchImageAsBase64(logoUrl);
    if (!base64) {
      datadogRum.addError("header-logo-fetch-failed", {
        reportId: reportData?.id,
        organizationLogoAllowed: exportData?.organizationLogoAllowed,
        hasOrganizationLogoUrl: !!exportData?.organizationLogoSignedUrl,
        hasTemplateLogoUrl: !!exportData?.templateLogoSignedUrl,
      });
      return null;
    }

    return base64;
  };

  const buildFileName = (clientName: string): string => {
    const now = new Date();
    const dateString = now
      .toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      })
      .replace(/\//g, "");

    if (isCatalightTemplate) return `${clientName} CCS Initial Assessment ${dateString}`;
    if (reportType === ApiReportReportTypeChoices.DiagnosisEvaluation)
      return `${clientName} Diagnosis Evaluation ${dateString}`;
    return `${clientName} Assessment ${dateString}`;
  };

  const generateDocxBlob = async (
    templateData: Record<string, TemplateDataValue>,
    templateUrl?: string,
  ): Promise<Blob> => {
    const presignedExportTemplateUrl = templateUrl ?? reportData?.reportTemplate?.templateResolvedUrl;
    if (!presignedExportTemplateUrl) throw new Error("No template URL available");

    const response = await fetch(presignedExportTemplateUrl);
    if (!response.ok) throw new Error("Failed to fetch export template");

    const buffer = await response.arrayBuffer();
    const zip = new PizZip(buffer);

    let doc: Docxtemplater;
    const imageDimensions = await resolveImageDimensions(templateData);

    try {
      const ImageModule = await import("docxtemplater-image-module-free");
      doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule.default(buildImageModuleOptions(imageDimensions))],
        nullGetter: () => "",
        ...(isCatalightTemplate ? {} : { parser: safeExpressionParser }),
      });
    } catch (moduleError) {
      console.warn("Image module not available, falling back to text:", moduleError);

      doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        nullGetter: () => "",
        ...(isCatalightTemplate ? {} : { parser: safeExpressionParser }),
      });

      replaceImageValuesWithPlaceholders(templateData, imageDimensions);
    }

    doc.render(templateData);
    return doc.getZip().generate({ type: "blob" });
  };

  const generateDocument = async (templateData: Record<string, TemplateDataValue>) => {
    const blob = await generateDocxBlob(templateData);
    const clientName = String(templateData?.clientName || "unknown");
    saveAs(blob, `${buildFileName(clientName)}.docx`);
  };

  const buildAndUploadDocx = async (
    templateData: Record<string, TemplateDataValue>,
    templateUrl?: string,
  ): Promise<string> => {
    const blob = await generateDocxBlob(templateData, templateUrl);
    const clientName = String(templateData?.clientName || "unknown");
    const fileName = buildFileName(clientName);
    const docxFile = new File([blob], `${fileName}.docx`, { type: DOCX_MIME });

    await generateReportPdfFromDocxMutation.mutateAsync({
      reportId: reportData.id,
      reportDocxFile: docxFile,
    });

    return fileName;
  };

  // Polls `reportStatus` until the server-side DOCX→PDF conversion finishes.
  // Uploading a fresh DOCX flips the report from COMPLETED → GENERATING; downstream
  // mutations (e.g. `startPayorSubmissionWorkflow`) reject non-COMPLETED reports,
  // so callers must wait for the status to flip back before chaining further work.
  const awaitPdfConversion = async (): Promise<void> => {
    const maxAttempts = 60;
    const pollingInterval = config.REPORT_POLLING_INTERVAL || 3000;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Wrap each poll in try/catch so transient network errors don't abort the
      // entire export — they count against maxAttempts and we keep retrying.
      // There are two timeout paths:
      //   1. Network error on the last attempt  → caught here, throws immediately
      //   2. Non-error status on the last attempt → falls through to the check below
      // Both produce the same "timed out" message; the asymmetry is intentional.
      let status: string | undefined;
      try {
        const { data } = await refetchReportStatus();
        status = data?.getReport?.edges?.[0]?.node?.reportStatus;
      } catch {
        if (attempt === maxAttempts - 1) throw new Error("PDF conversion timed out");
        await new Promise((resolve) => setTimeout(resolve, pollingInterval));
        continue;
      }

      // Skip the first attempt — the workflow may not have moved the report out of
      // COMPLETED yet (status was reset to GENERATING server-side but a stale cache
      // hit could still return the old COMPLETED value).
      if (attempt > 0 && status === ApiReportReportStatusChoices.Completed) return;
      if (status === ApiReportReportStatusChoices.Failure) throw new Error("PDF conversion failed on the server");
      if (attempt === maxAttempts - 1) throw new Error("PDF conversion timed out");

      await new Promise((resolve) => setTimeout(resolve, pollingInterval));
    }
  };

  const generateDocumentAsPdf = async (templateData: Record<string, TemplateDataValue>, templateUrl?: string) => {
    const fileName = await buildAndUploadDocx(templateData, templateUrl);

    await awaitPdfConversion();

    // Fetch the updated report to get the signed PDF URL
    const updatedReport = await refetchReport?.();
    const pdfUrl = updatedReport?.reportPdfFileSignedUrl;
    if (!pdfUrl) throw new Error("PDF URL not available after conversion");

    // A freshly-regenerated PDF is re-uploaded to the same signed URL, so a normal
    // fetch can get a browser-cached 304 for the *previous* file's bytes. Bypass the
    // cache so re-exporting after new data lands doesn't silently return stale content.
    const pdfResponse = await fetch(pdfUrl, { cache: "no-store" });
    if (!pdfResponse.ok) throw new Error("Failed to download converted PDF");
    const pdfBlob = await pdfResponse.blob();
    saveAs(pdfBlob, `${fileName}.pdf`);
  };

  const ensureExportData = async () => {
    if (reportExportData?.reportExportData) return reportExportData.reportExportData;
    const { data: freshData } = await refetchReportExportData();
    if (!freshData?.reportExportData) {
      throw new Error("No export data provided to processExportData.");
    }
    return freshData.reportExportData;
  };

  const buildTemplateData = async (): Promise<Record<string, TemplateDataValue>> => {
    const exportData = await ensureExportData();
    const headerLogo = await getHeaderLogoBase64(exportData);

    if (isCatalightTemplate) {
      const organizedCatalightGoals = organizeCatalightTreatmentPlanGoals(reportData);
      const catalightFieldMap = getCatalightFieldMap(exportData);

      const organizedKeys = Object.keys(organizedCatalightGoals);
      const catalightKeys = Object.keys(catalightFieldMap);
      const duplicateKeys = organizedKeys.filter((key) => catalightKeys.includes(key));
      if (duplicateKeys.length > 0) {
        throw new Error(`Duplicate keys found between data objects: ${duplicateKeys.join(", ")}`);
      }

      verifyUniqueFieldMapKeys(organizedCatalightGoals);
      verifyUniqueFieldMapKeys(catalightFieldMap);

      return {
        ...organizedCatalightGoals,
        ...catalightFieldMap,
        ...(headerLogo && { headerLogo }),
      };
    }

    let fieldMap = processExportData(reportData, exportData, font);
    fieldMap = {
      ...fieldMap,
      signatureName: signatureName,
      formattedSignatureName: signatureValue,
      ...(headerLogo && { headerLogo }),
    };
    if (fieldMap.signatureDate) {
      fieldMap.signatureDate = format(new Date(String(fieldMap.signatureDate)), "MM-dd-yyyy");
    }
    verifyUniqueFieldMapKeys(fieldMap);
    return fieldMap;
  };

  // Used by the Catalight "Submit to Payor" flow when the report has no PDF yet.
  // Uploads the DOCX and then waits for the server-side conversion to complete so
  // `reportStatus` flips back to COMPLETED before the caller kicks off
  // `startPayorSubmissionWorkflow` — that mutation rejects non-COMPLETED reports
  // with "Report is not completed".
  //
  // `isExporting` is toggled around the full call so callers can disable their submit
  // button during the multi-second window, preventing duplicate uploads.
  const generateAndUploadPdf = async (): Promise<void> => {
    setIsExporting(true);
    try {
      // Refetch the report to get a fresh presigned template URL. The URL from
      // the initial page load expires after 1 hour and the closure won't see a
      // re-render-updated value mid-flight, so we use the return value directly.
      const freshReport = await refetchReport?.();
      const freshTemplateUrl = freshReport?.reportTemplate?.templateResolvedUrl ?? undefined;
      const templateData = await buildTemplateData();
      await buildAndUploadDocx(templateData, freshTemplateUrl);
      await awaitPdfConversion();
    } finally {
      setIsExporting(false);
    }
  };

  const runExport = async (): Promise<boolean> => {
    if (!reportData) {
      console.error("Report data is not available");
      return false;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      const templateData = await buildTemplateData();

      if (isCatalightTemplate && exportType === ExportTypeEnum.Pdf) {
        const freshReport = await refetchReport?.();
        const freshTemplateUrl = freshReport?.reportTemplate?.templateResolvedUrl ?? undefined;
        await generateDocumentAsPdf(templateData, freshTemplateUrl);
      } else {
        await generateDocument(templateData);
      }
      return true;
    } catch (error) {
      datadogRum.addError(error, { type: "handleExportError" });
      console.error("Error preparing export data:", error);
      setExportError(error instanceof Error ? error.message : "Export failed. Please try again.");
      return false;
    } finally {
      setIsExporting(false);
      onExportComplete?.();
    }
  };

  // PROA-2622: this is the mutating export path -- `buildAndUploadDocx`
  // uploads a fresh DOCX, which flips the report COMPLETED -> GENERATING
  // server-side, then polls it back. A double click therefore knocks the
  // report out of COMPLETED twice and races two conversions. Shares the
  // Export button in ClinicalReportExport with the download-only path.
  const handleExport = useInFlightOnce(runExport);

  return {
    handleExport,
    generateAndUploadPdf,
    isExporting,
    isLoading,
    isExportDataReady,
    exportError,
    clearExportError: () => setExportError(null),
  };
};
